import React, { useEffect, useState } from "react";
import { Building2, Mail, Phone, Calendar, Download, Trash2, RefreshCcw, MessageSquare, ExternalLink, Hash } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const STATUSES = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "quoted", label: "Quoted" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

const STATUS_STYLES = {
  new: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  quoted: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  won: "bg-[#CFF048]/15 text-[#CFF048] border-[#CFF048]/40",
  lost: "bg-red-500/15 text-red-400 border-red-500/30",
};

function StatusPill({ status }) {
  const s = (status || "new").toLowerCase();
  const cls = STATUS_STYLES[s] || STATUS_STYLES.new;
  return (
    <span className={`label-mono ${cls} border px-2 py-0.5 text-[10px] uppercase`}>{s}</span>
  );
}

function InquiryDetail({ inq, onClose, onUpdate, onDelete }) {
  const [status, setStatus] = useState(inq.status || "new");
  const [notes, setNotes] = useState(inq.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/bulk/${inq.id}`, { status, admin_notes: notes });
      toast.success("UPDATED");
      onUpdate(r.data);
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const sizes = inq.size_breakdown || {};
  const sizeStr = ["S", "M", "L", "XL", "XXL"]
    .filter((s) => sizes[s])
    .map((s) => `${s}:${sizes[s]}`)
    .join(" · ") || "—";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0A0A0A] border border-[#27272A] w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-detail-modal"
      >
        <div className="sticky top-0 bg-[#0A0A0A] z-10 flex items-center justify-between px-6 py-4 border-b border-[#27272A]">
          <div className="min-w-0">
            <p className="label-mono text-[#CFF048]">// BULK INQUIRY</p>
            <h2 className="font-display font-black text-2xl truncate">{inq.company_name}</h2>
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-white" data-testid="close-bulk-detail">
            <ExternalLink size={18} className="rotate-180" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Contact */}
          <div className="border border-[#27272A] p-4 bg-black">
            <p className="label-mono text-[#71717A] mb-3">// CONTACT</p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <p><span className="label-mono text-[#71717A] mr-2">NAME</span>{inq.contact_name}</p>
              <p className="flex items-center gap-2"><Mail size={14} className="text-[#CFF048]" /><a href={`mailto:${inq.email}`} className="hover:text-[#CFF048] truncate">{inq.email}</a></p>
              {inq.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-[#CFF048]" /><a href={`tel:${inq.phone}`} className="hover:text-[#CFF048]">{inq.phone}</a></p>}
              {inq.industry && <p><span className="label-mono text-[#71717A] mr-2">INDUSTRY</span>{inq.industry}</p>}
            </div>
          </div>

          {/* Spec */}
          <div className="border border-[#27272A] p-4 bg-black">
            <p className="label-mono text-[#71717A] mb-3">// SPEC</p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <p><span className="label-mono text-[#71717A] mr-2">STYLE</span>{inq.tee_style}</p>
              <p><span className="label-mono text-[#71717A] mr-2">QTY</span><span className="text-[#CFF048] font-bold">{inq.quantity}</span></p>
              <p><span className="label-mono text-[#71717A] mr-2">COLOR</span>{inq.color}</p>
              <p><span className="label-mono text-[#71717A] mr-2">PRINT</span>{inq.print_method || "—"}</p>
              <p><span className="label-mono text-[#71717A] mr-2">PLACEMENT</span>{inq.placement || "—"}</p>
              <p><span className="label-mono text-[#71717A] mr-2">DEADLINE</span>{inq.deadline || "—"}</p>
              <p><span className="label-mono text-[#71717A] mr-2">BUDGET</span>{inq.budget_range || "—"}</p>
              <p><span className="label-mono text-[#71717A] mr-2">SIZES</span>{sizeStr}</p>
            </div>
          </div>

          {/* Notes from customer */}
          {inq.notes && (
            <div className="border border-[#27272A] p-4 bg-black">
              <p className="label-mono text-[#71717A] mb-2">// CUSTOMER NOTES</p>
              <p className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{inq.notes}</p>
            </div>
          )}

          {/* Design + mockup */}
          {(inq.design_url || inq.mockup_url) && (
            <div className="border border-[#27272A] p-4 bg-black">
              <p className="label-mono text-[#71717A] mb-3">// ASSETS</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {inq.design_url && (
                  <a
                    href={absoluteImg(inq.design_url)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block border border-[#27272A] p-3 hover:border-[#CFF048] transition-colors"
                    data-testid="bulk-design-link"
                  >
                    <p className="label-mono text-[#CFF048] mb-2 flex items-center gap-2">
                      <Download size={12} /> CUSTOMER DESIGN
                    </p>
                    {/\.(png|jpe?g|webp|svg)$/i.test(inq.design_url) ? (
                      <img src={absoluteImg(inq.design_url)} alt="" className="w-full max-h-[160px] object-contain" />
                    ) : (
                      <p className="label-mono text-[#71717A] text-xs">// VIEW FILE →</p>
                    )}
                  </a>
                )}
                {inq.mockup_url && (
                  <div className="border border-[#CFF048] p-3" data-testid="bulk-mockup-img">
                    <p className="label-mono text-[#CFF048] mb-2">// AI MOCKUP PREVIEW</p>
                    <img src={absoluteImg(inq.mockup_url)} alt="" className="w-full max-h-[160px] object-contain" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin controls */}
          <div className="border border-[#CFF048]/30 p-4 bg-black">
            <p className="label-mono text-[#CFF048] mb-3">// PIPELINE</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-mono text-[#71717A] mb-2 block">STATUS</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="cult-input"
                  data-testid="bulk-status-select"
                >
                  {STATUSES.filter((s) => s.id !== "all").map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <p className="label-mono text-[#71717A]">CREATED // {new Date(inq.created_at).toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-4">
              <label className="label-mono text-[#71717A] mb-2 block flex items-center gap-2">
                <MessageSquare size={12} /> ADMIN NOTES (PRIVATE)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="cult-input"
                placeholder="Quote sent · followup on · payment received ..."
                data-testid="bulk-admin-notes"
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={save}
                disabled={saving}
                className="btn-neon disabled:opacity-50"
                data-testid="bulk-save-btn"
              >
                {saving ? "SAVING..." : "SAVE"}
              </button>
              <button
                onClick={() => {
                  if (window.confirm("Delete this inquiry permanently?")) onDelete(inq.id);
                }}
                className="border border-[#27272A] text-[#71717A] font-mono px-4 py-2 hover:border-red-500 hover:text-red-400 transition-colors flex items-center gap-2"
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={14} /> DELETE
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminBulk() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, quoted: 0, won: 0, lost: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, ct] = await Promise.all([
        api.get(`/admin/bulk?status=${tab}`),
        api.get("/admin/bulk/counts"),
      ]);
      setItems(list.data || []);
      setCounts(ct.data || {});
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const handleUpdate = (updated) => {
    setItems((cur) => cur.map((i) => (i.id === updated.id ? updated : i)));
    setActive(updated);
    load();
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/bulk/${id}`);
      toast.success("DELETED");
      setActive(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AdminLayout title="Bulk Orders · Pipeline">
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="bulk-stats">
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// NEW</p>
          <p className="font-display font-black text-2xl text-yellow-400 mt-1">{counts.new || 0}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// CONTACTED</p>
          <p className="font-display font-black text-2xl text-blue-300 mt-1">{counts.contacted || 0}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// QUOTED</p>
          <p className="font-display font-black text-2xl text-violet-300 mt-1">{counts.quoted || 0}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// WON</p>
          <p className="font-display font-black text-2xl text-[#CFF048] mt-1">{counts.won || 0}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// LOST</p>
          <p className="font-display font-black text-2xl text-red-400 mt-1">{counts.lost || 0}</p>
        </div>
        <button onClick={load} className="ml-auto border border-[#27272A] px-3 py-2 label-mono hover:border-[#CFF048] hover:text-[#CFF048] flex items-center gap-2" data-testid="bulk-refresh">
          <RefreshCcw size={14} /> REFRESH
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#27272A]" data-testid="bulk-tabs">
        {STATUSES.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 label-mono uppercase transition-colors -mb-px border-b-2 ${
              tab === t.id ? "text-[#CFF048] border-[#CFF048]" : "text-[#71717A] border-transparent hover:text-white"
            }`}
            data-testid={`bulk-tab-${t.id}`}
          >
            {t.label}
            {t.id === "new" && counts.new > 0 && (
              <span className="ml-2 bg-[#CFF048] text-black text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm">
                {counts.new}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center label-mono text-[#71717A] py-20">// LOADING...</div>
      ) : items.length === 0 ? (
        <div className="border border-[#27272A] p-12 text-center label-mono text-[#71717A]" data-testid="bulk-empty">
          // NO {tab.toUpperCase()} INQUIRIES
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0A0A0A] overflow-x-auto" data-testid="bulk-list">
          <table className="w-full text-sm">
            <thead className="border-b border-[#27272A] bg-black">
              <tr className="label-mono text-[#71717A]">
                <th className="text-left p-3">COMPANY</th>
                <th className="text-left p-3 hidden md:table-cell">CONTACT</th>
                <th className="text-left p-3">QTY</th>
                <th className="text-left p-3 hidden lg:table-cell">STYLE</th>
                <th className="text-left p-3 hidden lg:table-cell">DEADLINE</th>
                <th className="text-left p-3">STATUS</th>
                <th className="text-left p-3 hidden md:table-cell">CREATED</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr
                  key={i.id}
                  onClick={() => setActive(i)}
                  className="border-b border-[#18181B] hover:bg-[#18181B] cursor-pointer transition-colors"
                  data-testid={`bulk-row-${i.id}`}
                >
                  <td className="p-3">
                    <div className="font-display font-bold flex items-center gap-2">
                      <Building2 size={14} className="text-[#CFF048]" />
                      {i.company_name}
                    </div>
                    {i.industry && <p className="label-mono text-[#71717A] mt-1">{i.industry}</p>}
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <p className="truncate">{i.contact_name}</p>
                    <p className="label-mono text-[#71717A] truncate">{i.email}</p>
                  </td>
                  <td className="p-3 font-display font-black text-[#CFF048]">
                    <span className="flex items-center gap-1"><Hash size={12} />{i.quantity}</span>
                  </td>
                  <td className="p-3 hidden lg:table-cell">{i.tee_style}</td>
                  <td className="p-3 hidden lg:table-cell label-mono text-[#71717A]">
                    {i.deadline ? (<span className="flex items-center gap-1"><Calendar size={12} />{i.deadline}</span>) : "—"}
                  </td>
                  <td className="p-3"><StatusPill status={i.status} /></td>
                  <td className="p-3 hidden md:table-cell label-mono text-[#71717A] whitespace-nowrap">
                    {new Date(i.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && (
        <InquiryDetail
          inq={active}
          onClose={() => setActive(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </AdminLayout>
  );
}
